import { Grid, List, ListItem, ListItemIcon, Switch, Tooltip, Typography } from '@material-ui/core'
import ListItemText from '@material-ui/core/ListItemText'
import FacebookIcon from '@material-ui/icons/Facebook'
import InstagramIcon from '@material-ui/icons/Instagram'
import TwitterIcon from '@material-ui/icons/Twitter'
import clsx from 'clsx'
import GoogleIcon from 'components/icons/googleIcon'
import _ from 'lodash'
import { useSnackbar } from 'notistack'
import React from 'react'
import { Field, Form, FormSpy } from 'react-final-form'
import { Trans, useTranslation } from 'react-i18next'
import { SubmitButtonProps } from '../../common/submitButton'
import { countHashtag } from '../../views/slate/slateUtils'
import TextField from '../components/textField'
import ValidityField from '../components/validityField'
import { useFormsStyles } from '../forms.css'
import { moveArrayItemToNewIndex } from '../helpers/moveArrayItemToNewIndex'
import { Image } from '../helpers/useImages'
import { AddFormImageState, AddFormState } from '../slate/addForm'
import PhotoContainer from '../slate/components/photoContainer'

interface Props {
    subscription?: { [property: string]: boolean }
    onSubmit: (values: AddNewsFormState) => void
    children: (content: React.ReactNode, actions: Array<SubmitButtonProps>) => React.ReactNode
    formClassName?: string
    inProgress: boolean
    canPublishMultiplePhoto: boolean
    initialValues: AddNewsFormState
    isFacebookLinked: boolean
    isInstagramLinked: boolean
    isGoogleLinked: boolean
    isFacebookSignedExpired: boolean
    isGoogleSignedExpired: boolean
    showInstagram: boolean
    showTwitter: boolean
    isTwitterLinked: boolean
    isTwitterSignedExpired: boolean
}

export interface AddNewsFormState {
    endTime: Date
    photos?: Array<AddFormImageState>
    comment?: string
    isGoogleShared: boolean
    isTwitterShared: boolean
    isFacebookShared: boolean
    isInstagramShared: boolean
}

export interface AddNewsFormError {
    photo?: string
    comment?: string
    endTime?: string
}

const AddNewsForm: React.FC<Props> = ({
    subscription = {
        submitting: true,
        pristine: true,
    },
    onSubmit,
    children,
    formClassName,
    inProgress,
    canPublishMultiplePhoto,
    initialValues,
    isFacebookLinked,
    isInstagramLinked,
    isGoogleLinked,
    isFacebookSignedExpired,
    isGoogleSignedExpired,
    showInstagram,
    showTwitter,
    isTwitterSignedExpired,
    isTwitterLinked,
}) => {
    const formsClasses = useFormsStyles()
    const { t } = useTranslation()
    const { enqueueSnackbar } = useSnackbar()

    const validate = (values: AddNewsFormState) => {
        const errors: AddNewsFormError = {}

        if (values.comment && countHashtag(values.comment) > 30) {
            errors.comment = t('form-validation.hashtagLimit')
        }
        if (!values.endTime) {
            errors.endTime = t('form-validation.endTime')
        }
        return errors
    }

    return (
        <Form
            initialValues={initialValues}
            subscription={subscription}
            // debug={console.log}
            validate={validate}
            onSubmit={onSubmit}
            keepDirtyOnReinitialize
            mutators={{
                moveEntry: (args, state, tools) => {
                    const from = args[0] as number
                    const to = args[1] as number
                    const {
                        formState: { values },
                    } = state
                    let { photos } = values as AddFormState
                    photos = moveArrayItemToNewIndex(photos ?? [], from, to)
                    const newPhotos: Array<AddFormImageState> = []
                    photos.forEach((photo, index) => {
                        newPhotos.push({
                            ...photo,
                            sortOrder: index,
                        })
                    })
                    tools.changeValue(state, 'photos', () => newPhotos)
                },
                deleteImage: (args, state, tools) => {
                    const image = args[0] as Image
                    const {
                        formState: { values },
                    } = state
                    const { photos } = values as AddFormState

                    const imageIndex = photos?.findIndex((photo) => photo.id === image.id) ?? -1
                    photos?.splice(imageIndex, 1)
                    tools.changeValue(state, 'photos', () => photos)
                },
                setShouldDelete: (args, state, tools) => {
                    const shouldDelete = args[0] as boolean
                    tools.changeValue(state, 'shouldDelete', () => (shouldDelete ? [true] : []))
                },
            }}
        >
            {({ handleSubmit, submitting, errors, form: { mutators } }) => {
                const submit = (event: any) => {
                    handleSubmit(event)
                    if (errors && !_.isEmpty(errors)) {
                        const errorEntries = Object.entries(errors)
                        if (errorEntries.length === 1) {
                            for (const [, value] of errorEntries) {
                                enqueueSnackbar(value, {
                                    variant: 'error',
                                })
                            }
                        } else {
                            enqueueSnackbar(t('form-validation.common'), {
                                variant: 'error',
                            })
                        }
                    }
                }
                return (
                    <form onSubmit={submit} noValidate className={formClassName}>
                        {children(
                            <Grid container spacing={4}>
                                {/* IMAGES */}
                                <Grid item sm={12} xs={12}>
                                    <PhotoContainer
                                        canPublishMultiplePhoto={canPublishMultiplePhoto}
                                        showTwitter={showTwitter}
                                        showInstagram={showInstagram}
                                        mutators={mutators}
                                        xl={2}
                                        xs={6}
                                        lg={3}
                                        sm={4}
                                    />
                                </Grid>
                                {/* DATE */}
                                <Grid item sm={6} xs={12}>
                                    <Typography
                                        color="textSecondary"
                                        className={clsx(formsClasses.formTitle, formsClasses.noPaddingTop)}
                                    >
                                        <Trans i18nKey="form.newsDate" />
                                    </Typography>

                                    <ValidityField name="endTime" />
                                </Grid>
                                {/*  COMMENT  */}
                                <Grid item sm={6} xs={12}>
                                    <Typography
                                        color="textSecondary"
                                        className={clsx(formsClasses.formTitle, formsClasses.noPaddingTop)}
                                    >
                                        <Trans i18nKey="form.newsComment" />
                                    </Typography>
                                    <TextField
                                        name="comment"
                                        multiline={true}
                                        maxRows={8}
                                        variant="outlined"
                                    />
                                </Grid>
                                <Grid item sm={6} xs={12} />
                                {/*  SHARING  */}
                                <Grid item sm={6} xs={12}>
                                    <Typography color="textSecondary" className={formsClasses.formTitle}>
                                        <Trans i18nKey="form.slateShare" />
                                    </Typography>
                                    <List>
                                        <Field name="isFacebookShared">
                                            {({ input: { value, onChange } }) => (
                                                <Tooltip
                                                    arrow
                                                    title={
                                                        !isFacebookLinked
                                                            ? (t('form.noFacebook') as string)
                                                            : isFacebookSignedExpired
                                                            ? (t('form.facebookExpired') as string)
                                                            : ''
                                                    }
                                                    placement="top-start"
                                                    classes={{
                                                        popper: formsClasses.shareMessageTop,
                                                    }}
                                                >
                                                    <div>
                                                        <ListItem
                                                            divider
                                                            button
                                                            onClick={() => onChange(!value)}
                                                            disabled={
                                                                !isFacebookLinked || isFacebookSignedExpired
                                                            }
                                                        >
                                                            <ListItemIcon>
                                                                <Switch
                                                                    color="primary"
                                                                    edge="start"
                                                                    checked={value}
                                                                    disableRipple
                                                                />
                                                            </ListItemIcon>
                                                            <ListItemIcon
                                                                classes={{
                                                                    root: formsClasses.shareIcon,
                                                                }}
                                                            >
                                                                <FacebookIcon
                                                                    className={formsClasses.facebookIcon}
                                                                />
                                                            </ListItemIcon>
                                                            <ListItemText
                                                                primary={t('actions.shareFacebook')}
                                                            />
                                                        </ListItem>
                                                    </div>
                                                </Tooltip>
                                            )}
                                        </Field>
                                        {showInstagram && (
                                            <FormSpy subscription={{ values: true }}>
                                                {(props) => (
                                                    <Field name="isInstagramShared">
                                                        {({ input: { value, onChange } }) => (
                                                            <FormSpy subscription={{ values: true }}>
                                                                {(props) => (
                                                                    <Tooltip
                                                                        arrow
                                                                        title={
                                                                            !isFacebookLinked
                                                                                ? (t(
                                                                                      'form.noFacebook',
                                                                                  ) as string)
                                                                                : !isInstagramLinked
                                                                                ? (t(
                                                                                      'form.noInstagram',
                                                                                  ) as string)
                                                                                : isFacebookSignedExpired
                                                                                ? (t(
                                                                                      'form.facebookExpired',
                                                                                  ) as string)
                                                                                : ''
                                                                        }
                                                                        placement="top-start"
                                                                        classes={{
                                                                            popper: formsClasses.shareMessageTop,
                                                                        }}
                                                                    >
                                                                        <div>
                                                                            <ListItem
                                                                                divider
                                                                                button
                                                                                onClick={() =>
                                                                                    onChange(!value)
                                                                                }
                                                                                disabled={
                                                                                    !isInstagramLinked ||
                                                                                    isFacebookSignedExpired
                                                                                }
                                                                            >
                                                                                <ListItemIcon>
                                                                                    <Switch
                                                                                        color="primary"
                                                                                        edge="start"
                                                                                        checked={
                                                                                            props.values
                                                                                                .photos &&
                                                                                            props.values
                                                                                                .photos
                                                                                                .length > 0
                                                                                                ? value
                                                                                                : false
                                                                                        }
                                                                                        disabled={
                                                                                            !props.values
                                                                                                .photos ||
                                                                                            (props.values
                                                                                                .photos &&
                                                                                                props.values
                                                                                                    .photos
                                                                                                    .length ===
                                                                                                    0)
                                                                                        }
                                                                                        disableRipple
                                                                                    />
                                                                                </ListItemIcon>
                                                                                <ListItemIcon
                                                                                    classes={{
                                                                                        root: formsClasses.shareIcon,
                                                                                    }}
                                                                                >
                                                                                    <InstagramIcon
                                                                                        className={
                                                                                            formsClasses.instagramIcon
                                                                                        }
                                                                                    />
                                                                                </ListItemIcon>
                                                                                <ListItemText
                                                                                    primary={t(
                                                                                        'actions.shareInstagram',
                                                                                    )}
                                                                                />
                                                                            </ListItem>
                                                                        </div>
                                                                    </Tooltip>
                                                                )}
                                                            </FormSpy>
                                                        )}
                                                    </Field>
                                                )}
                                            </FormSpy>
                                        )}

                                        <Field name="isGoogleShared">
                                            {({ input: { value, onChange } }) => (
                                                <Tooltip
                                                    arrow
                                                    title={
                                                        !isGoogleLinked
                                                            ? (t('form.noGoogle') as string)
                                                            : isGoogleSignedExpired
                                                            ? (t('form.googleExpired') as string)
                                                            : ''
                                                    }
                                                    placement="bottom-start"
                                                    classes={{
                                                        popper: formsClasses.shareMessageBottom,
                                                    }}
                                                >
                                                    <div>
                                                        <ListItem
                                                            divider={showTwitter}
                                                            button
                                                            onClick={() => onChange(!value)}
                                                            disabled={
                                                                !isGoogleLinked || isGoogleSignedExpired
                                                            }
                                                        >
                                                            <ListItemIcon>
                                                                <Switch
                                                                    color="primary"
                                                                    edge="start"
                                                                    checked={value}
                                                                    disableRipple
                                                                />
                                                            </ListItemIcon>
                                                            <ListItemIcon
                                                                classes={{
                                                                    root: formsClasses.shareIcon,
                                                                }}
                                                            >
                                                                <GoogleIcon />
                                                            </ListItemIcon>
                                                            <ListItemText
                                                                primary={t('actions.shareGoogle')}
                                                            />
                                                        </ListItem>
                                                    </div>
                                                </Tooltip>
                                            )}
                                        </Field>

                                        {showTwitter && (
                                            <Field name="isTwitterShared">
                                                {({ input: { value, onChange } }) => (
                                                    <Tooltip
                                                        arrow
                                                        title={
                                                            !isTwitterLinked
                                                                ? (t('form.noTwitter') as string)
                                                                : isTwitterSignedExpired
                                                                ? (t('form.twitterExpired') as string)
                                                                : ''
                                                        }
                                                        placement="top-start"
                                                        classes={{
                                                            popper: formsClasses.shareMessageTop,
                                                        }}
                                                    >
                                                        <div>
                                                            <ListItem
                                                                button
                                                                onClick={() => onChange(!value)}
                                                                disabled={
                                                                    !isTwitterLinked || isTwitterSignedExpired
                                                                }
                                                            >
                                                                <ListItemIcon>
                                                                    <Switch
                                                                        color="primary"
                                                                        edge="start"
                                                                        checked={value}
                                                                        disableRipple
                                                                    />
                                                                </ListItemIcon>
                                                                <ListItemIcon
                                                                    classes={{
                                                                        root: formsClasses.shareIcon,
                                                                    }}
                                                                >
                                                                    <TwitterIcon
                                                                        className={formsClasses.twitterIcon}
                                                                    />
                                                                </ListItemIcon>
                                                                <ListItemText
                                                                    primary={t('actions.shareTwitter')}
                                                                />
                                                            </ListItem>
                                                        </div>
                                                    </Tooltip>
                                                )}
                                            </Field>
                                        )}
                                    </List>
                                </Grid>
                            </Grid>,
                            [
                                {
                                    type: 'submit',
                                    loading: submitting || inProgress,
                                    className: formsClasses.submit,
                                    children: <Trans i18nKey="actions.validate" />,
                                },
                            ],
                        )}
                    </form>
                )
            }}
        </Form>
    )
}

export default AddNewsForm
